import { IClientData } from "../../../../models/ClientData";
import { IQuote } from "../../../../models/QuoteData";
import { ageToDateOfBirth } from "../../../Utils/ageToDateOfBirth";

export const codMinAge = 54;
export const codMaxAge = 99;
export const codDelayOptions = Array.from(
  { length: 10 },
  (_, index) => index + 1
);
export const codGrowthOptions = Array.from({ length: 9 }, (_, index) => index);
export const codDefaultDelayYears = 3;
export const codDefaultGrowthRate = 5;
/** The user's address isn't known until booking, so illustration quotes use a fixed postcode. */
export const codIllustrationPostcode = "DN4 5NL";
/** Maximum tax-free cash, as the original calculator's quotes assumed. */
export const codTaxFreeCashTypeID = "5904";

export type CodPotMode = "growth" | "contributions";
export type CodContributionMode = "amount" | "salary";

/**
 * The calculator's own answers, stored in the draft under "cod.*" and never sent with a lead. Answers that are client
 * fields (dateOfBirth, sex, annuity.pensionFund) use their standard names so they prefill across the landers.
 */
export interface CodAnswers {
  delayYears?: string | number;
  growthRate?: string | number;
  potMode?: CodPotMode;
  contributionMode?: CodContributionMode;
  monthlyContribution?: string | number;
  salary?: string | number;
  salaryPercent?: string | number;
}

export interface CodInputs {
  age: number;
  sex: "Male" | "Female";
  pot: number;
  delayYears: number;
  growthRate: number;
  potMode: CodPotMode;
  monthlyAmount: number;
}

/** Illustration quotes kept with the journey (journeyData.illustration). */
export interface CodIllustration {
  inputsKey: string;
  current: IQuote[];
  delayed: IQuote[];
  quoteDate: string;
}

const shortMonths = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export const codMoney = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  maximumFractionDigits: 0,
});

export function parseAmount(value: unknown) {
  if (value === undefined || value === null) return null;
  const text = String(value);
  const amount = Number(text.replace(/[£,\s]/g, ""));
  return text.trim() && Number.isFinite(amount) ? amount : null;
}

export function delayText(years: number) {
  return `${years} ${years === 1 ? "year" : "years"}`;
}

export function delayOptionText(years: number, today = new Date()) {
  return `${delayText(years)} (${shortMonths[today.getMonth()]} ${
    today.getFullYear() + years
  })`;
}

export function ageText(age: number, years = 0) {
  const totalMonths = Math.round(years * 12);
  const laterAge = age + Math.floor(totalMonths / 12);
  const remainingMonths = totalMonths % 12;
  if (!remainingMonths) return String(laterAge);
  return `${laterAge} and ${remainingMonths} months`;
}

export function getMonthlyContribution(answers: CodAnswers) {
  if (answers.potMode !== "contributions") return 0;
  if ((answers.contributionMode ?? "amount") === "amount")
    return parseAmount(answers.monthlyContribution) ?? 0;
  return (
    ((parseAmount(answers.salary) ?? 0) *
      ((parseAmount(answers.salaryPercent) ?? 0) / 100)) /
    12
  );
}

/** The calculator's inputs from the journey, or null when a step hasn't been answered. */
export function getCodInputs(lead: IClientData): CodInputs | null {
  const answers = ((lead as IClientData & { cod?: CodAnswers }).cod ??
    {}) as CodAnswers;
  const birthYear = Number(/^(\d{4})-/.exec(lead.dateOfBirth ?? "")?.[1]);
  const age = birthYear ? new Date().getFullYear() - birthYear : null;
  const pot = parseAmount(lead.annuity?.pensionFund);
  const delayYears = parseAmount(answers.delayYears ?? codDefaultDelayYears);
  const growthRate = parseAmount(answers.growthRate ?? codDefaultGrowthRate);
  if (
    age === null ||
    pot === null ||
    delayYears === null ||
    growthRate === null
  )
    return null;

  return {
    age,
    sex: lead.sex === "Female" ? "Female" : "Male",
    pot,
    delayYears,
    growthRate,
    potMode: answers.potMode === "contributions" ? "contributions" : "growth",
    monthlyAmount: getMonthlyContribution(answers),
  };
}

/** Pot after the delay: annual compounding on the pot, monthly compounding on contributions. */
export function projectPot({
  pot,
  delayYears,
  growthRate,
  potMode,
  monthlyAmount,
}: CodInputs) {
  const months = delayYears * 12;
  const annualGrowth = growthRate / 100;
  const growthOnlyPot = pot * Math.pow(1 + annualGrowth, delayYears);
  if (potMode !== "contributions") return growthOnlyPot;
  const monthlyGrowth = annualGrowth / 12;
  const contributionGrowth = monthlyGrowth
    ? monthlyAmount *
      ((Math.pow(1 + monthlyGrowth, months) - 1) / monthlyGrowth)
    : monthlyAmount * months;
  return growthOnlyPot + contributionGrowth;
}

export function getInputsKey(inputs: CodInputs) {
  return JSON.stringify(inputs);
}

/** Lifetime annuities only, highest income first. */
export function getBestQuote(quotes: IQuote[]) {
  return [...quotes]
    .filter(
      (quote) =>
        quote.productName !== "Fixed Term Annuity" &&
        Number(quote.annuityAmountPA) > 0
    )
    .sort((a, b) => Number(b.annuityAmountPA) - Number(a.annuityAmountPA))[0];
}

export function deriveResults(
  inputs: CodInputs,
  illustration: CodIllustration
) {
  const bestNow = getBestQuote(illustration.current);
  const bestLater = getBestQuote(illustration.delayed);
  if (!bestNow || !bestLater) return null;

  const incomeNow = Number(bestNow.annuityAmountPA);
  const incomeLater = Number(bestLater.annuityAmountPA);
  const missedIncome = incomeNow * inputs.delayYears;
  const incomeIncrease = incomeLater - incomeNow;
  const paybackYears =
    incomeIncrease > 0 ? missedIncome / incomeIncrease : null;
  const roundedPaybackYears =
    paybackYears === null ? null : Math.round(paybackYears * 10) / 10;
  const paybackYearsLabel =
    roundedPaybackYears === null
      ? null
      : Number.isInteger(roundedPaybackYears)
      ? String(roundedPaybackYears)
      : roundedPaybackYears.toFixed(1);
  const breakEvenAge =
    paybackYears === null
      ? null
      : Math.round(inputs.age + inputs.delayYears + paybackYears);

  return {
    bestNow,
    incomeNow,
    incomeLater,
    missedIncome,
    incomeIncrease,
    paybackYears,
    roundedPaybackYears,
    paybackYearsLabel,
    breakEvenAge,
  };
}

/** Quote request for the illustration endpoint; `ageAtQuote` is the age the annuity would start. */
export function buildIllustrationRequest(
  inputs: CodInputs,
  ageAtQuote: number,
  fund: number
): IClientData {
  return {
    dateOfBirth: ageToDateOfBirth(ageAtQuote),
    sex: inputs.sex,
    postcode: codIllustrationPostcode,
    annuity: {
      pensionFund: String(Math.round(fund)),
      taxFreeCashTypeID: codTaxFreeCashTypeID,
      setQuoteOptions: "false",
    },
  } as IClientData;
}

/**
 * Every lead payload this lander sends (LanderV2CampaignConfig.transformLeadPayload): the calculator's own answers
 * become standard lead fields, and the rest stay out of the CRM.
 */
export function codLeadPayload(payload: IClientData): IClientData {
  const {
    cod,
    codMonthlyContributionPreview: _preview,
    resubmission: _resubmission,
    ...rest
  } = payload as IClientData & {
    cod?: CodAnswers;
    codMonthlyContributionPreview?: unknown;
    resubmission?: unknown;
  };
  const delayYears = parseAmount(cod?.delayYears);
  const { plannedRetirementDate, ...annuity } = rest.annuity ?? {};

  // The CRM only takes a real date: the booking form's month and year once chosen, otherwise 1 January of the year
  // the delay reaches. Before the delay is known the field is left out rather than sent empty.
  const retirementDate = /^\d{4}-\d{2}-\d{2}$/.test(plannedRetirementDate ?? "")
    ? plannedRetirementDate
    : delayYears === null
    ? undefined
    : `${new Date().getFullYear() + delayYears}-01-01`;

  return {
    ...rest,
    annuity: {
      ...annuity,
      ...(retirementDate ? { plannedRetirementDate: retirementDate } : {}),
      taxFreeCashTypeID: codTaxFreeCashTypeID,
    },
  };
}
